// ─── Garde de sauvegarde des phases ──────────────────────────────────────────
//
// Module PUR (pas de React, pas de Wasp) : decide ce qu'il faut faire AVANT
// d'ecrire une phase en base.
//
// LE BUG QUE CE MODULE EXISTE POUR SUPPRIMER : le formulaire du template garde
// toute sa saisie en memoire React (nom, couleurs, types de compte, paliers) et
// ne l'ecrit qu'au submit du `<form>`. Or `PhaseEditor` est monte DANS ce
// formulaire et ecrit, lui, IMMEDIATEMENT : il appelle `upsertPropFirmPhase`
// puis `onRefresh`, qui reinjecte le template frais venu du serveur. Toute
// saisie non encore soumise etait donc ecrasee par la version en base — elle
// n'avait jamais quitte le navigateur. Un associe a perdu son travail ainsi :
// il a ajoute une phase, et le reste du formulaire est revenu a l'etat d'avant.
//
// POURQUOI sauver le template AVANT la phase plutot que de bloquer le bouton :
// la phase se rattache a un palier qui doit exister en base (`tierId`), donc
// l'ordre template-puis-phase est de toute facon impose. Le faire
// automatiquement evite a l'admin de retenir une procedure, et supprime la
// classe de bug entiere plutot que de la signaler.
//
// POURQUOI la validation bloquante est consultee ici : sauver le template peut
// ECHOUER (nom vide, palier incoherent). Si on lancait l'upsert de la phase
// malgre tout, on ecrirait la phase puis on rafraichirait — et on reproduirait
// exactement la perte qu'on corrige. Un blocage doit donc interrompre la
// sequence AVANT la moindre ecriture.

/**
 * Projette un template (venu du SERVEUR ou construit par le formulaire) sur une
 * forme comparable, pour detecter une saisie non enregistree.
 *
 * POURQUOI une projection explicite plutot qu'un `JSON.stringify` direct des
 * deux objets : ils n'ont pas la meme forme. Le template serveur porte des
 * `createdAt`, des `phases`, des `id` de palier ; le payload du formulaire porte
 * des `clientKey` et des nombres deja parses. Les comparer bruts rendrait le
 * formulaire eternellement « modifie », et la garde se declencherait a chaque
 * ajout de phase, y compris sans la moindre saisie.
 *
 * Les PHASES sont volontairement exclues : elles sont ecrites par leur propre
 * action, hors du payload du template. Les inclure ferait passer le formulaire
 * pour modifie juste apres une sauvegarde de phase reussie.
 */
export function serializeTemplateState(template: any): unknown {
  if (!template) return null;

  const commissions = template.symbolCommissions ?? null;

  return {
    name: (template.name ?? "").trim(),
    color: template.color ?? null,
    textColor: template.textColor ?? null,
    // La bascule custom <-> systeme n'est presente dans le payload que
    // lorsqu'elle CHANGE : `undefined` le reste du temps, d'ou le `?? null`
    // qui garde la projection stable. Sans cette ligne, cocher la case puis
    // ajouter une phase rendrait la bascule invisible a `isDirty` : le guard
    // repondrait « save-phase-only », la promotion ne serait jamais envoyee,
    // et le refresh qui suit effacerait la case.
    isSystem: template.isSystem ?? null,
    logoUrl: (template.logoUrl ?? "").trim() || null,
    supportedPlatforms: [...(template.supportedPlatforms ?? [])].sort(),
    commissionPerContract:
      template.commissionPerContract != null ? Number(template.commissionPerContract) : null,
    payoutDestination: template.payoutDestination === "wallet" ? "wallet" : "direct",
    // Les cles d'un objet n'ont pas d'ordre garanti d'une source a l'autre.
    symbolCommissions:
      commissions && typeof commissions === "object"
        ? Object.entries(commissions as Record<string, unknown>)
            .map(([k, v]) => [k.trim().toUpperCase(), Number(v)] as const)
            .sort((a, b) => a[0].localeCompare(b[0]))
        : null,
    accountTypes: (template.accountTypes ?? []).map((at: any, i: number) => ({
      id: at.id ?? null,
      name: at.name ?? "",
      slug: at.slug ?? "",
      sortOrder: i,
      isActive: at.isActive ?? true,
      pricingModel: at.pricingModel ?? "one_time",
      drawdownLockOffset: at.drawdownLockOffset != null ? Number(at.drawdownLockOffset) : null,
      drawdownLockMode: at.drawdownLockMode ?? null,
      drawdownLockScope: at.drawdownLockScope ?? null,
      tiers: (at.tiers ?? []).map((t: any) => serializeTier(t)),
    })),
  };
}

/**
 * Normalise une colonne `symbolCommissions` (Json en base, objet brut issu du
 * formulaire) en une forme comparable ou l'ordre des cles n'a pas d'incidence.
 * `null`, `undefined` et `{}` sont tous la meme absence de surcharge : un
 * template serveur sans commissions ne doit jamais paraitre "modifie" face a
 * un formulaire non touche.
 *
 * Meme politique que la normalisation firm-level de `serializeTemplateState`
 * (cles en MAJUSCULES, valeurs via `Number()`, tri par cle) — reutilisee ici
 * telle quelle pour le palier afin que les deux niveaux restent coherents.
 */
function normalizeSymbolCommissions(raw: unknown): readonly (readonly [string, number])[] | null {
  if (!raw || typeof raw !== "object") return null;
  const entries = Object.entries(raw as Record<string, unknown>)
    .map(([k, v]) => [k.trim().toUpperCase(), Number(v)] as const)
    .sort((a, b) => a[0].localeCompare(b[0]));
  return entries.length > 0 ? entries : null;
}

/**
 * Un palier, reduit aux champs que le formulaire sait ecrire.
 *
 * Tolere les deux provenances : le palier SERVEUR porte des colonnes plates
 * (`maxTotalLossAmount` / `maxTotalLossPercent`), le palier issu de
 * `tierFormToData` porte les memes colonnes — c'est justement ce que
 * `tierFormToData` reconstitue. La projection reste donc symetrique.
 */
function serializeTier(tier: any): unknown {
  const num = (v: unknown) => (v == null || v === "" ? null : Number(v));

  // ⚠️ Cette liste doit rester alignee sur la sortie de `tierFormToData`. Un
  // champ present la-bas et absent ici rendrait une modification de ce champ
  // INVISIBLE a `isDirty` : l'admin le saisirait, ajouterait une phase, et le
  // perdrait — exactement le bug que ce module corrige, reduit a un champ.
  // `sortOrder` est exclu a dessein : il est derive de la position, jamais
  // saisi.
  return {
    label: (tier.label ?? "").trim(),
    minBalance: num(tier.minBalance),
    maxBalance: num(tier.maxBalance),
    accountSize: num(tier.accountSize),
    profitTargetPercent: num(tier.profitTargetPercent),
    profitTargetAmount: num(tier.profitTargetAmount),
    minTradingDays: num(tier.minTradingDays),
    drawdownType: tier.drawdownType || null,
    hasTrailingDrawdown: tier.hasTrailingDrawdown ?? false,
    maxDailyLossPercent: num(tier.maxDailyLossPercent),
    maxDailyLossAmount: num(tier.maxDailyLossAmount),
    maxTotalLossPercent: num(tier.maxTotalLossPercent),
    maxTotalLossAmount: num(tier.maxTotalLossAmount),
    trailingDrawdownPercent: num(tier.trailingDrawdownPercent),
    trailingDrawdownAmount: num(tier.trailingDrawdownAmount),
    payoutThresholdPercent: num(tier.payoutThresholdPercent),
    payoutThresholdAmount: num(tier.payoutThresholdAmount),
    payoutCooldownDays: num(tier.payoutCooldownDays),
    activationFee: num(tier.activationFee),
    consistencyRulePercent: num(tier.consistencyRulePercent),
    fundedConsistencyRulePercent: num(tier.fundedConsistencyRulePercent),
    maxContracts: num(tier.maxContracts),
    price: num(tier.price),
    commissionPerContract: num(tier.commissionPerContract),
    symbolCommissions: normalizeSymbolCommissions(tier.symbolCommissions),
  };
}

/** Ce que l'appelant doit faire avant d'ecrire la phase. */
export type PhaseSaveGuard =
  | {
      /** Enregistrer le template d'abord, puis la phase. */
      action: "save-template-first";
    }
  | {
      /** Ecrire la phase directement : rien en attente ne peut etre perdu. */
      action: "save-phase-only";
    }
  | {
      /**
       * Ne rien ecrire du tout : le template en cours est invalide, donc
       * insauvegardable, donc sa saisie serait perdue par le refresh.
       */
      action: "block";
      /** Message a montrer, deja formule pour l'admin. */
      reason: string;
      blockingCount: number;
    };

export interface PhaseSaveContext {
  /**
   * Le formulaire porte-t-il des modifications jamais envoyees au serveur ?
   * Inclut la creation d'un template non encore persiste.
   */
  hasUnsavedTemplateChanges: boolean;
  /** Problemes bloquants du formulaire template, tels que deja collectes. */
  blockingProblems: { tierLabel: string; message: string }[];
}

/**
 * Decide de la marche a suivre avant d'ecrire une phase.
 *
 * Volontairement sans effet de bord : c'est ce qui la rend testable sans DOM,
 * le projet n'ayant pas de stack de test React.
 */
export function guardPhaseSave(ctx: PhaseSaveContext): PhaseSaveGuard {
  if (!ctx.hasUnsavedTemplateChanges) {
    return { action: "save-phase-only" };
  }

  if (ctx.blockingProblems.length > 0) {
    const first = ctx.blockingProblems[0];
    return {
      action: "block",
      reason: first.tierLabel ? `${first.tierLabel} : ${first.message}` : first.message,
      blockingCount: ctx.blockingProblems.length,
    };
  }

  return { action: "save-template-first" };
}
